'use client'

import { useEffect, useRef } from 'react'

import { Experience } from './review-flow/experience'
import type { ReviewModalLabels } from './review-flow/types'

export interface RatingFormLabels extends ReviewModalLabels {
  updateYourReview: string
  loadingReviewForm: string
}

interface RatingFormProps {
  value: number
  message: string
  onChange: (score: number) => void
  onMessage: (message: string) => void
  onContinue: () => void
  busy: boolean
  actionLabel: string
  errorMessage?: string
  labels: RatingFormLabels
}

export const RatingForm = ({
  value,
  message,
  onChange,
  onMessage,
  onContinue,
  busy,
  actionLabel,
  errorMessage,
  labels,
}: RatingFormProps) => {
  const error = useRef<HTMLParagraphElement>(null)
  useEffect(() => {
    if (errorMessage) {
      error.current?.focus()
    }
  }, [errorMessage])

  return (
    <form
      className="rounded-2xl border border-navy-20 bg-white"
      onSubmit={(event) => {
        event.preventDefault()
        if (value > 0 && !busy) {
          onContinue()
        }
      }}
    >
      <fieldset disabled={busy} className="p-5 sm:p-8">
        <Experience score={value} message={message} onScore={onChange} onMessage={onMessage} labels={labels} />
        {errorMessage && (
          <p
            ref={error}
            tabIndex={-1}
            role="alert"
            className="mt-5 rounded-xl bg-cream p-4 text-[16px] text-navy outline-none"
          >
            {errorMessage}
          </p>
        )}
      </fieldset>
      <div className="flex justify-end border-t border-navy-10 px-5 py-4 sm:px-8">
        <button
          type="submit"
          data-tracking="ignore"
          disabled={value < 1 || busy}
          aria-busy={busy || undefined}
          className="inline-flex min-h-[48px] w-full cursor-pointer items-center justify-center rounded-full bg-navy px-6 py-3 text-[16px] font-semibold text-white transition-colors hover:bg-navy-90 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-lapis disabled:cursor-default disabled:opacity-60 sm:w-auto"
        >
          {actionLabel}
        </button>
      </div>
    </form>
  )
}
